import { PAYMENT_STEP_TYPE, FEATURES } from 'config'
import {
  calculateDiscountedCost,
  calculatePaymentAmount,
  calculateReferralCreditsUsed,
  isUpgradeType,
  isFeatureEnabled,
} from 'utils'
import {
  FREE_MEMBERSHIP_TYPES,
  TRAILER_CARE_DETAIL,
  TRAILER_CARE_TYPE_NO_UPGRADE,
  TRAILER_CARE_TYPE_NONE,
} from 'types'

const NO_COST_TRAILER_CARE_TYPES = [
  TRAILER_CARE_TYPE_NO_UPGRADE,
  TRAILER_CARE_TYPE_NONE,
]

const computeBillingDetails = ({
  account,
  billing,
  discountCodeDetails,
  membershipPrices,
  paymentStepType,
  referralCredits,
  selectedMembershipType,
  selectedTrailerCareType,
  trailerCarePrices,
  donationAmount = 0,
}) => {
  const isUpgrade = isUpgradeType(paymentStepType)
  const isMembershipUpgrade =
    paymentStepType === PAYMENT_STEP_TYPE.MEMBERSHIP_UPGRADE

  // If this is a membership only upgrade or if this is not an upgrade at all,
  // the original membership cost is the base price of the membership type
  // selected. For a trailer care upgrade, the membership cost should be 0.
  let originalMembershipCost = 0
  if (isMembershipUpgrade || !isUpgrade) {
    const price = membershipPrices?.[selectedMembershipType]
    // Paid types must resolve a price; a missing one stays undefined so it's
    // caught before charging. Only free selections (trials) legitimately cost 0.
    originalMembershipCost = FREE_MEMBERSHIP_TYPES.includes(selectedMembershipType)
      ? price ?? 0
      : price
  }

  const finalDonationAmount = isUpgrade
    ? 0
    : donationAmount || billing?.finalDonation || 0

  const discountedMembershipCost = calculateDiscountedCost(
    originalMembershipCost,
    discountCodeDetails,
  )

  // Declining trailer care is free, so use 0. A real selection with no price
  // stays undefined so it's caught before charging.
  const trailerCareCost =
    !selectedTrailerCareType ||
    NO_COST_TRAILER_CARE_TYPES.includes(selectedTrailerCareType)
      ? 0
      : trailerCarePrices?.[selectedTrailerCareType]
  const subtotalWithoutDonations =
    (discountedMembershipCost ?? 0) + (trailerCareCost ?? 0)

  const isReferralEnabled = isFeatureEnabled(FEATURES.REFERRAL_PROGRAM)
  const referralCreditsUsed = isReferralEnabled
    ? calculateReferralCreditsUsed(
        subtotalWithoutDonations,
        referralCredits ?? account?.referral_credits__c,
      )
    : 0

  const trailerCareDetail = TRAILER_CARE_DETAIL[selectedTrailerCareType]

  const totalPayment = calculatePaymentAmount({
    discountedMembershipCost,
    referralCreditsUsed,
    subtotal: subtotalWithoutDonations,
    donationAmount: finalDonationAmount,
  })

  return {
    discountedMembershipCost,
    finalDonation: finalDonationAmount,
    originalMembershipCost,
    referralCreditsUsed,
    subtotalWithoutDonations,
    totalPayment,
    trailerCareCost,
    trailerCareDetail,
  }
}

export default computeBillingDetails
